import { MAX_IMPORT_ROWS } from "../config";
import type { CsvPreview, DuplicateHeaderGroup } from "../types";

/** Thrown when a file exceeds `MAX_IMPORT_ROWS`; carries the (partial) count seen. */
export class ImportRowLimitError extends Error {
  constructor(public readonly limit: number) {
    super(
      `This file has more than ${limit.toLocaleString()} rows. Split it into smaller files and import them separately.`,
    );
    this.name = "ImportRowLimitError";
  }
}

/**
 * Lowercase forms of the accepted component-identifying column headers, in
 * priority order. "Component Name" is canonical; "Deduction Name" is a legacy
 * alias so existing deduction exports keep working.
 */
const COMPONENT_NAME_HEADERS_LC = ["component name", "deduction name"];

/**
 * Split a single CSV line into fields.
 *
 * Handles:
 * - Unquoted fields separated by commas
 * - Double-quoted fields (may contain commas)
 * - Escaped double-quotes ("") inside quoted fields
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let i = 0;

  while (i < line.length) {
    if (line[i] === '"') {
      // Quoted field — collect until the closing (unescaped) double-quote
      let field = "";
      i++; // skip opening quote
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            // Escaped double-quote inside the field
            field += '"';
            i += 2;
          } else {
            // Closing quote
            i++; // skip closing quote
            break;
          }
        } else {
          field += line[i];
          i++;
        }
      }
      fields.push(field);
      // Skip the comma that follows this field (if any)
      if (i < line.length && line[i] === ",") i++;
    } else {
      // Unquoted field — everything up to the next comma (or end of line)
      const commaIdx = line.indexOf(",", i);
      if (commaIdx === -1) {
        fields.push(line.slice(i));
        break;
      } else {
        fields.push(line.slice(i, commaIdx));
        i = commaIdx + 1;
      }
    }
  }

  // A trailing comma means there is an empty final field
  if (line.length > 0 && line[line.length - 1] === ",") {
    fields.push("");
  }

  return fields;
}

/**
 * Make repeated header names unique (Excel-style) so row maps and Map Columns
 * keys stay one-to-one with source columns (#225).
 *
 * First occurrence keeps the original name; repeats become `"Name (2)"`, `"Name (3)"`, …
 */
export function disambiguateHeaders(trimmed: string[]): string[] {
  const used = new Set<string>();
  return trimmed.map((header) => {
    if (!used.has(header)) {
      used.add(header);
      return header;
    }
    let n = 2;
    while (used.has(`${header} (${n})`)) n++;
    const unique = `${header} (${n})`;
    used.add(unique);
    return unique;
  });
}

/**
 * Header names that appear more than once in the source row (after trim).
 * Used to warn on the Upload step before disambiguated names are shown (#225).
 */
export function findDuplicateHeaders(trimmed: string[]): DuplicateHeaderGroup[] {
  const counts = new Map<string, number>();
  for (const header of trimmed) {
    counts.set(header, (counts.get(header) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** User-facing warning when {@link findDuplicateHeaders} is non-empty. */
export function formatDuplicateHeadersWarning(
  groups: DuplicateHeaderGroup[],
): string {
  if (groups.length === 0) return "";
  const list = groups.map((g) => `${g.name} (${g.count} columns)`).join(", ");
  return (
    `Duplicate column names found: ${list}. ` +
    "Each column is renamed in the preview (for example, Amount, Amount (2)) " +
    "so you can map them separately. Use the preview table below to see which " +
    "value belongs to which column."
  );
}

/**
 * Trim raw header fields, detect duplicates, then disambiguate for preview keys.
 */
function prepareHeaders(rawHeaderFields: string[]): {
  headers: string[];
  duplicateHeaders: DuplicateHeaderGroup[];
} {
  const trimmed = rawHeaderFields.map((h) => h.trim());
  return {
    duplicateHeaders: findDuplicateHeaders(trimmed),
    headers: disambiguateHeaders(trimmed),
  };
}

/**
 * Parse raw CSV text into a {@link CsvPreview} for the Upload & Preview screen.
 *
 * - Header names are **trimmed** of surrounding whitespace. Repeated names are
 *   disambiguated (`Amount`, `Amount (2)`, …) so each source column maps to a
 *   distinct key in preview rows and on the Map Columns step (#225).
 * - `componentColumn` is the detected component-identifying header (canonical
 *   "Component Name" or the legacy "Deduction Name"), or null if none is present.
 * - `componentNames` contains the **distinct** values from that column,
 *   preserving first-seen order.
 * - Returns empty arrays for an empty file or a header-only file.
 *
 * **NOTE**: The server owns authoritative CSV parsing. This utility is for the
 * client-side preview table and to derive distinct component names for parameter
 * discovery — it does not replace server-side validation.
 */
export function parseCsv(raw: string): CsvPreview {
  // Normalise line endings then split
  const lines = raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

  // Find the first non-empty line (the header row), skipping any leading blanks.
  const headerIdx = lines.findIndex((line) => line.trim() !== "");
  if (headerIdx === -1) {
    return {
      headers: [],
      rows: [],
      componentNames: [],
      componentColumn: null,
      duplicateHeaders: [],
    };
  }

  // Parse, trim, and disambiguate duplicate header names
  const rawHeaderFields = splitCsvLine(lines[headerIdx]);
  const { headers, duplicateHeaders } = prepareHeaders(rawHeaderFields);

  if (headers.length === 0 || headers.every((h) => h === "")) {
    return {
      headers: [],
      rows: [],
      componentNames: [],
      componentColumn: null,
      duplicateHeaders: [],
    };
  }

  // Locate the component-identifying column (case-insensitive), trying the
  // canonical "Component Name" header first, then the legacy "Deduction Name".
  let componentNameIdx = -1;
  for (const candidate of COMPONENT_NAME_HEADERS_LC) {
    componentNameIdx = headers.findIndex((h) => h.toLowerCase() === candidate);
    if (componentNameIdx !== -1) break;
  }
  const componentColumn =
    componentNameIdx !== -1 ? headers[componentNameIdx] : null;

  // Parse data rows, skipping blank lines
  const rows: Record<string, string>[] = [];
  const seenComponentNames = new Set<string>();
  const componentNames: string[] = [];

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;

    const values = splitCsvLine(line);
    const row: Record<string, string> = {};

    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (values[j] ?? "").trim();
    }

    rows.push(row);

    // Collect distinct component names in first-seen order
    if (componentNameIdx !== -1) {
      const name = (values[componentNameIdx] ?? "").trim();
      if (name !== "" && !seenComponentNames.has(name)) {
        seenComponentNames.add(name);
        componentNames.push(name);
      }
    }
  }

  return { headers, rows, componentNames, componentColumn, duplicateHeaders };
}

/** Number of data lines processed per event-loop yield in {@link parseCsvStreaming}. */
const STREAM_BATCH_SIZE = 2000;

/**
 * Async, non-blocking variant of {@link parseCsv} for large files.
 *
 * Parses in batches, yielding to the event loop between them so the UI stays
 * responsive on 10k+ row files. Enforces `MAX_IMPORT_ROWS` — throws
 * {@link ImportRowLimitError} once the limit is exceeded (before building the
 * full row set). Output shape is identical to `parseCsv`.
 */
export async function parseCsvStreaming(raw: string): Promise<CsvPreview> {
  const lines = raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

  const headerIdx = lines.findIndex((line) => line.trim() !== "");
  if (headerIdx === -1) {
    return {
      headers: [],
      rows: [],
      componentNames: [],
      componentColumn: null,
      duplicateHeaders: [],
    };
  }

  const { headers, duplicateHeaders } = prepareHeaders(
    splitCsvLine(lines[headerIdx]),
  );
  if (headers.length === 0 || headers.every((h) => h === "")) {
    return {
      headers: [],
      rows: [],
      componentNames: [],
      componentColumn: null,
      duplicateHeaders: [],
    };
  }

  let componentNameIdx = -1;
  for (const candidate of COMPONENT_NAME_HEADERS_LC) {
    componentNameIdx = headers.findIndex((h) => h.toLowerCase() === candidate);
    if (componentNameIdx !== -1) break;
  }
  const componentColumn =
    componentNameIdx !== -1 ? headers[componentNameIdx] : null;

  const rows: Record<string, string>[] = [];
  const seenComponentNames = new Set<string>();
  const componentNames: string[] = [];

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;

    const values = splitCsvLine(line);
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (values[j] ?? "").trim();
    }
    rows.push(row);

    if (rows.length > MAX_IMPORT_ROWS) {
      throw new ImportRowLimitError(MAX_IMPORT_ROWS);
    }

    if (componentNameIdx !== -1) {
      const name = (values[componentNameIdx] ?? "").trim();
      if (name !== "" && !seenComponentNames.has(name)) {
        seenComponentNames.add(name);
        componentNames.push(name);
      }
    }

    // Yield to the event loop each batch so a large file doesn't block the UI.
    if (rows.length % STREAM_BATCH_SIZE === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return { headers, rows, componentNames, componentColumn, duplicateHeaders };
}
